/* eslint-disable no-console */
import { arrayToMap } from "@excalidraw/common";

import { getCommonBounds } from "./bounds";
import { syncMovedIndices } from "./fractionalIndex";
import { getAllNestedFrameChildren, moveFrameChildren } from "./frame";
import { getBoundTextElement } from "./textElement";
import { computeBoundTextPosition } from "./textElement";
import {
  isFrameLikeElement,
  isBoundToContainer,
  hasBoundTextElement,
} from "./typeChecks";
import { newElementWith } from "./mutateElement";
import { getMaximumGroups } from "./groups";
import { CaptureUpdateAction } from "./store";

import type {
  ExcalidrawElement,
  ExcalidrawFrameElement,
  ExcalidrawFrameLikeElement,
  AutoLayoutProperties,
  AutoLayoutDirection,
  AutoLayoutAlignmentPosition,
  AutoLayoutGap,
  ElementsMap,
} from "./types";

/**
 * Default auto layout properties
 */
export const DEFAULT_AUTO_LAYOUT_PROPERTIES: AutoLayoutProperties = {
  enabled: false,
  direction: "vertical",
  gap: 10,
  padding: {
    top: 16,
    right: 16,
    bottom: 16,
    left: 16,
  },
  alignmentPosition: "center-center",
  resizing: {
    width: "hug",
    height: "hug",
  },
};

/**
 * Helper function to convert alignment position to individual axis alignments
 */
const getAlignmentFromPosition = (
  position: AutoLayoutAlignmentPosition,
  direction: AutoLayoutDirection,
): { horizontal: string; vertical: string } => {
  const alignmentMap: Record<
    AutoLayoutAlignmentPosition,
    { horizontal: string; vertical: string }
  > = {
    "top-left": { horizontal: "start", vertical: "start" },
    "top-center": { horizontal: "center", vertical: "start" },
    "top-right": { horizontal: "end", vertical: "start" },
    "center-left": { horizontal: "start", vertical: "center" },
    "center-center": { horizontal: "center", vertical: "center" },
    "center-right": { horizontal: "end", vertical: "center" },
    "bottom-left": { horizontal: "start", vertical: "end" },
    "bottom-center": { horizontal: "center", vertical: "end" },
    "bottom-right": { horizontal: "end", vertical: "end" },
  };

  return alignmentMap[position];
};

/**
 * Check if gap is auto (space-between mode)
 */
const isAutoGap = (gap: AutoLayoutGap): gap is "auto" => {
  return gap === "auto";
};

/**
 * Get numeric gap value (returns 0 for auto gap)
 */
const getNumericGap = (gap: AutoLayoutGap): number => {
  return typeof gap === "number" ? gap : 0;
};

/**
 * Check if a frame has auto layout enabled
 */
export const isAutoLayoutFrame = (
  element: ExcalidrawElement,
): element is ExcalidrawFrameElement => {
  return element.type === "frame" && element.autoLayout?.enabled === true;
};

/**
 * Check if an element is inside an auto layout frame
 */
export const isElementInAutoLayoutFrame = (
  element: ExcalidrawElement,
  elementsMap: ElementsMap,
): boolean => {
  let currentId = element.frameId;
  while (currentId) {
    const frame = elementsMap.get(currentId);
    if (!frame || !isFrameLikeElement(frame)) {
      return false;
    }
    if (isAutoLayoutFrame(frame)) {
      return true;
    }
    currentId = frame.frameId;
  }
  return false;
};

/**
 * Get children elements of a frame that are eligible for auto layout
 */
export const getAutoLayoutChildren = (
  frame: ExcalidrawFrameElement,
  allElements: readonly ExcalidrawElement[],
  elementsMap: ElementsMap,
): ExcalidrawElement[] => {
  return allElements.filter(
    (element) =>
      element.frameId === frame.id &&
      !element.isDeleted &&
      element.type !== "selection" &&
      // Exclude bound text elements - they should be positioned relative to their container
      !isBoundToContainer(element) &&
      // Skip elements with zero dimensions to prevent layout issues
      element.width > 0 &&
      element.height > 0,
  );
};

/**
 * Get the order index of an element in the scene
 */
const getElementOrderIndex = (
  element: ExcalidrawElement,
  allElements: readonly ExcalidrawElement[],
): number => {
  return allElements.findIndex((el) => el.id === element.id);
};

/**
 * Detect if elements have been manually reordered by checking if their positions
 * match their document order
 */
const detectManualReordering = (
  groups: Array<{
    group: ExcalidrawElement[];
    bounds: readonly [number, number, number, number];
    orderIndex: number;
  }>,
  direction: AutoLayoutDirection,
): boolean => {
  if (groups.length <= 1) {
    return false;
  }

  // Check if the current positional order matches the document order
  const positionSorted = [...groups].sort((a, b) => {
    if (direction === "horizontal") {
      // Use center position for more accurate ordering
      const aCenterX = (a.bounds[0] + a.bounds[2]) / 2;
      const bCenterX = (b.bounds[0] + b.bounds[2]) / 2;
      return aCenterX - bCenterX;
    }
    // Use center position for more accurate ordering
    const aCenterY = (a.bounds[1] + a.bounds[3]) / 2;
    const bCenterY = (b.bounds[1] + b.bounds[3]) / 2;
    return aCenterY - bCenterY;
  });

  const documentSorted = [...groups].sort(
    (a, b) => a.orderIndex - b.orderIndex,
  );

  // Track if there are any freedom drawings (pencil lines) in the groups
  const hasFreedomDrawings = groups.some(({ group }) =>
    group.some((element) => element.type === "freedraw"),
  );

  // Count significant reorders - be more conservative to avoid interfering with auto-layout
  let significantReorders = 0;
  const totalElements = groups.length;

  // For auto-layout frames, require more significant reordering before considering it manual
  // This prevents interference with moveFrameChildren when elements are just being added
  const reorderThreshold = Math.max(2, Math.ceil(totalElements * 0.5)); // At least 50% need to be reordered

  // Calculate what percentage of elements are out of order
  for (let i = 0; i < groups.length; i++) {
    const positionIndex = positionSorted.findIndex(
      (g) => g.group[0].id === documentSorted[i].group[0].id,
    );

    // More conservative detection - require larger position differences
    const positionDifference = Math.abs(positionIndex - i);

    if (hasFreedomDrawings) {
      // For freedom drawings, require very significant reordering
      if (positionDifference > Math.max(3, totalElements * 0.4)) {
        significantReorders++;
      }
    } else {
      // For regular elements in auto-layout, require at least 2 position difference
      // to avoid triggering on small additions/insertions
      // eslint-disable-next-line no-lonely-if
      if (positionDifference >= 2) {
        significantReorders++;
      }
    }
  }

  // Require a significant number of elements to be reordered before considering it manual
  // This helps preserve document order for auto-layout and prevents interference with moveFrameChildren
  return significantReorders >= reorderThreshold;
};

/**
 * Calculate the layout for auto layout children
 */
export const calculateAutoLayout = (
  frame: ExcalidrawFrameElement,
  children: ExcalidrawElement[],
  elementsMap: ElementsMap,
  allElements?: readonly ExcalidrawElement[],
): {
  updatedChildren: ExcalidrawElement[];
  updatedFrame: ExcalidrawFrameElement;
  reorderedElements?: ExcalidrawElement[]; // Elements that need document order update
  allUpdatedElements: ExcalidrawElement[]; // All elements that were updated (including moved children)
} => {
  if (!frame.autoLayout?.enabled || children.length === 0) {
    return {
      updatedChildren: children,
      updatedFrame: frame,
      allUpdatedElements: children,
    };
  }

  const autoLayout = frame.autoLayout;
  const { direction, gap, padding, alignmentPosition } = autoLayout;

  // Convert position-based alignment to individual axis alignments
  const alignment = getAlignmentFromPosition(alignmentPosition, direction);

  // Group elements (handle grouped elements as single units)
  const groups = getMaximumGroups(children, elementsMap, true); // exclude bound text for auto layout

  // Check if we're dealing with grouped elements that may have been dragged
  // If elements in a group have inconsistent spacing, it likely means they were dragged
  const groupsWithRelativePositions = groups.map((group) => {
    const groupBounds = getCommonBounds(group);

    // For groups with multiple elements, store their relative positions
    const relativePositions =
      group.length > 1
        ? group.map((element) => ({
            element,
            relativeX: element.x - groupBounds[0],
            relativeY: element.y - groupBounds[1],
          }))
        : null;

    return {
      group,
      bounds: groupBounds,
      relativePositions,
      // Use the first element in the group to determine order
      orderIndex: allElements ? getElementOrderIndex(group[0], allElements) : 0,
    };
  });

  // Check if there are any pencil/freedom drawings in the elements
  const hasFreedomDrawings = children.some(
    (element) => element.type === "freedraw",
  );

  // When we have freedom drawings, we'll be more careful about position-based sorting
  // to preserve document order when appropriate

  // Detect if elements have been manually reordered
  const hasManualReordering =
    allElements &&
    detectManualReordering(
      groupsWithRelativePositions.map((g) => ({
        group: g.group,
        bounds: g.bounds,
        orderIndex: g.orderIndex,
      })),
      direction,
    );

  // Sort groups based on whether manual reordering was detected
  // For freedom drawings, always prefer document order unless there's clear manual reordering
  const sortedGroups = [...groupsWithRelativePositions].sort((a, b) => {
    // When we have freedom drawings and no clear manual reordering, preserve document order
    if (hasFreedomDrawings && !hasManualReordering && allElements) {
      return a.orderIndex - b.orderIndex;
    }
    if (hasManualReordering) {
      // If elements were manually reordered, use current position to respect user intent
      if (direction === "horizontal") {
        // For groups with multiple elements, use edge-based detection for earlier swapping
        const aIsGroup = a.group.length > 1;
        const bIsGroup = b.group.length > 1;

        // Get comparison points based on whether items are groups
        let aComparePoint: number;
        let bComparePoint: number;

        if (aIsGroup && !bIsGroup) {
          // Group vs single: use group's leading edge vs single's center
          aComparePoint = a.bounds[0]; // left edge of group
          bComparePoint = (b.bounds[0] + b.bounds[2]) / 2; // center of single
        } else if (!aIsGroup && bIsGroup) {
          // Single vs group: use single's center vs group's trailing edge
          aComparePoint = (a.bounds[0] + a.bounds[2]) / 2; // center of single
          bComparePoint = b.bounds[2]; // right edge of group
        } else if (aIsGroup && bIsGroup) {
          // Group vs group: use edges for earlier detection
          const aCenterX = (a.bounds[0] + a.bounds[2]) / 2;
          const bCenterX = (b.bounds[0] + b.bounds[2]) / 2;
          // If A is moving right (A's center > B's center), use A's left edge vs B's center
          // If A is moving left (A's center < B's center), use A's right edge vs B's center
          if (aCenterX > bCenterX) {
            aComparePoint = a.bounds[0]; // left edge
            bComparePoint = bCenterX; // center
          } else {
            aComparePoint = a.bounds[2]; // right edge
            bComparePoint = bCenterX; // center
          }
        } else {
          // Single vs single: use centers
          aComparePoint = (a.bounds[0] + a.bounds[2]) / 2;
          bComparePoint = (b.bounds[0] + b.bounds[2]) / 2;
        }

        return aComparePoint - bComparePoint;
      }
      // Vertical layout
      const aIsGroup = a.group.length > 1;
      const bIsGroup = b.group.length > 1;

      let aComparePoint: number;
      let bComparePoint: number;

      if (aIsGroup && !bIsGroup) {
        // Group vs single: use group's leading edge vs single's center
        aComparePoint = a.bounds[1]; // top edge of group
        bComparePoint = (b.bounds[1] + b.bounds[3]) / 2; // center of single
      } else if (!aIsGroup && bIsGroup) {
        // Single vs group: use single's center vs group's trailing edge
        aComparePoint = (a.bounds[1] + a.bounds[3]) / 2; // center of single
        bComparePoint = b.bounds[3]; // bottom edge of group
      } else if (aIsGroup && bIsGroup) {
        // Group vs group: use edges for earlier detection
        const aCenterY = (a.bounds[1] + a.bounds[3]) / 2;
        const bCenterY = (b.bounds[1] + b.bounds[3]) / 2;
        // If A is moving down (A's center > B's center), use A's top edge vs B's center
        // If A is moving up (A's center < B's center), use A's bottom edge vs B's center
        if (aCenterY > bCenterY) {
          aComparePoint = a.bounds[1]; // top edge
          bComparePoint = bCenterY; // center
        } else {
          aComparePoint = a.bounds[3]; // bottom edge
          bComparePoint = bCenterY; // center
        }
      } else {
        // Single vs single: use centers
        aComparePoint = (a.bounds[1] + a.bounds[3]) / 2;
        bComparePoint = (b.bounds[1] + b.bounds[3]) / 2;
      }

      return aComparePoint - bComparePoint;
    } else if (allElements) {
      // If no manual reordering, use document order to preserve sequence
      return a.orderIndex - b.orderIndex;
    }
    // Fallback: sort by current position in the layout direction
    if (direction === "horizontal") {
      return a.bounds[0] - b.bounds[0];
    }
    return a.bounds[1] - b.bounds[1];
  });

  // Calculate new positions
  const isHorizontal = direction === "horizontal";
  const frameStart = isHorizontal ? frame.x : frame.y;
  const frameCrossStart = isHorizontal ? frame.y : frame.x;
  const frameCrossSize = isHorizontal ? frame.height : frame.width;
  const frameMainSize = isHorizontal ? frame.width : frame.height;
  const crossPadding = isHorizontal
    ? { start: padding.top, end: padding.bottom }
    : { start: padding.left, end: padding.right };
  const mainPadding = isHorizontal
    ? { start: padding.left, end: padding.right }
    : { start: padding.top, end: padding.bottom };

  // Calculate content area for cross-axis alignment
  const contentCrossSize =
    frameCrossSize - crossPadding.start - crossPadding.end;
  const contentMainSize = frameMainSize - mainPadding.start - mainPadding.end;

  // For fixed-size frames, use absolute positioning like Figma
  const isMainAxisFixed = isHorizontal
    ? autoLayout.resizing.width === "fixed"
    : autoLayout.resizing.height === "fixed";

  const updatedChildren: ExcalidrawElement[] = [];

  // Create a lookup map for element group membership
  const elementIdToGroupIds = new Map<string, string[]>();
  groups.forEach((group) => {
    const groupIds = group.map((el) => el.id);
    group.forEach((el) => {
      elementIdToGroupIds.set(el.id, groupIds);
    });
  });

  // First pass: calculate how much space fill elements need
  // We need to consider groups as a whole for fill behavior
  const fillGroupsMainAxis = sortedGroups.filter((groupData) => {
    const groupResizing = (groupData.group[0] as any).autoLayoutChildResizing;
    return isHorizontal
      ? groupResizing?.width === "fill"
      : groupResizing?.height === "fill";
  });

  const nonFillGroupsMainAxis = sortedGroups.filter((groupData) => {
    const groupResizing = (groupData.group[0] as any).autoLayoutChildResizing;
    return isHorizontal
      ? groupResizing?.width !== "fill"
      : groupResizing?.height !== "fill";
  });

  // Calculate space taken by non-fill elements
  const nonFillMainAxisSize = nonFillGroupsMainAxis.reduce((acc, groupData) => {
    const bounds = groupData.bounds;
    return acc + (isHorizontal ? bounds[2] - bounds[0] : bounds[3] - bounds[1]);
  }, 0);

  // Calculate available space for fill elements
  // Gap can be negative to create overlap. For sizing purposes we treat
  // negative gaps as 0 so we don't accidentally allow Fill elements to spill
  // into the padding area (which would visually look like padding isn't
  // working).
  const numericGap = getNumericGap(gap);
  const totalGapSpace =
    Math.max(0, sortedGroups.length - 1) * Math.max(0, numericGap);

  const availableSpace = Math.max(
    0,
    contentMainSize - nonFillMainAxisSize - totalGapSpace,
  );
  const fillElementSize =
    fillGroupsMainAxis.length > 0
      ? Math.max(1, availableSpace / fillGroupsMainAxis.length)
      : 0;

  // Calculate positions based on gap type
  let positions: number[] = [];

  if (isAutoGap(gap)) {
    // Space-between: distribute elements evenly
    if (sortedGroups.length === 1) {
      // Single element: position based on alignment
      const mainAlignment = isHorizontal
        ? alignment.horizontal
        : alignment.vertical;

      // Calculate size considering fill behavior
      const group = sortedGroups[0].group;
      const hasMainAxisFill = isHorizontal
        ? (group[0] as any).autoLayoutChildResizing?.width === "fill"
        : (group[0] as any).autoLayoutChildResizing?.height === "fill";

      const totalContentSize = hasMainAxisFill
        ? fillElementSize
        : sortedGroups[0].bounds[isHorizontal ? 2 : 0] -
          sortedGroups[0].bounds[isHorizontal ? 0 : 1];

      // For fixed-size frames, use absolute positioning like Figma
      if (isMainAxisFixed) {
        switch (mainAlignment) {
          case "start":
            positions = [mainPadding.start];
            break;
          case "center":
            positions = [
              mainPadding.start + (contentMainSize - totalContentSize) / 2,
            ];
            break;
          case "end":
            positions = [
              mainPadding.start + contentMainSize - totalContentSize,
            ];
            break;
        }
      } else {
        // For hug-content frames, use the original flexbox-style alignment
        switch (mainAlignment) {
          case "start":
            positions = [mainPadding.start];
            break;
          case "center":
            positions = [
              mainPadding.start + (contentMainSize - totalContentSize) / 2,
            ];
            break;
          case "end":
            positions = [
              mainPadding.start + contentMainSize - totalContentSize,
            ];
            break;
        }
      }
    } else {
      // Multiple elements: space-between distribution

      // For fixed-size frames with auto gap, still use space-between but apply alignment to the distributed group
      // eslint-disable-next-line no-lonely-if
      if (isMainAxisFixed) {
        const totalGroupSize = sortedGroups.reduce((sum, { bounds, group }) => {
          // Check if this group has fill elements
          const hasMainAxisFill = isHorizontal
            ? (group[0] as any).autoLayoutChildResizing?.width === "fill"
            : (group[0] as any).autoLayoutChildResizing?.height === "fill";

          const groupSize = hasMainAxisFill
            ? fillElementSize
            : isHorizontal
            ? bounds[2] - bounds[0]
            : bounds[3] - bounds[1];
          return sum + groupSize;
        }, 0);

        const availableSpace = contentMainSize - totalGroupSize;
        const spaceBetween = availableSpace / (sortedGroups.length - 1);

        // For fixed-size frames, still use space-between but consider alignment
        // This maintains the space-between behavior while respecting the frame size
        let currentPos = mainPadding.start;
        positions = sortedGroups.map(({ bounds, group }, index) => {
          const pos = currentPos;

          // Check if this group has fill elements
          const hasMainAxisFill = isHorizontal
            ? (group[0] as any).autoLayoutChildResizing?.width === "fill"
            : (group[0] as any).autoLayoutChildResizing?.height === "fill";

          const groupSize = hasMainAxisFill
            ? fillElementSize
            : isHorizontal
            ? bounds[2] - bounds[0]
            : bounds[3] - bounds[1];

          currentPos +=
            groupSize + (index < sortedGroups.length - 1 ? spaceBetween : 0);
          return pos;
        });
      } else {
        // For hug-content frames, use space-between distribution
        const totalGroupSize = sortedGroups.reduce((sum, { bounds, group }) => {
          // Check if this group has fill elements
          const hasMainAxisFill = isHorizontal
            ? (group[0] as any).autoLayoutChildResizing?.width === "fill"
            : (group[0] as any).autoLayoutChildResizing?.height === "fill";

          const groupSize = hasMainAxisFill
            ? fillElementSize
            : isHorizontal
            ? bounds[2] - bounds[0]
            : bounds[3] - bounds[1];
          return sum + groupSize;
        }, 0);

        const availableSpace = contentMainSize - totalGroupSize;
        const spaceBetween = availableSpace / (sortedGroups.length - 1);

        let currentPos = mainPadding.start;
        positions = sortedGroups.map(({ bounds, group }, index) => {
          const pos = currentPos;

          // Check if this group has fill elements
          const hasMainAxisFill = isHorizontal
            ? (group[0] as any).autoLayoutChildResizing?.width === "fill"
            : (group[0] as any).autoLayoutChildResizing?.height === "fill";

          const groupSize = hasMainAxisFill
            ? fillElementSize
            : isHorizontal
            ? bounds[2] - bounds[0]
            : bounds[3] - bounds[1];

          currentPos +=
            groupSize + (index < sortedGroups.length - 1 ? spaceBetween : 0);
          return pos;
        });
      }
    }
  } else {
    // Fixed gap: normal positioning
    // eslint-disable-next-line no-console

    // For fixed-size frames, apply alignment to the entire group like Figma
    // eslint-disable-next-line no-lonely-if
    if (isMainAxisFixed) {
      // eslint-disable-next-line no-console

      const totalGroupSize = sortedGroups.reduce((sum, { bounds, group }) => {
        // Check if this group has fill elements
        const hasMainAxisFill = isHorizontal
          ? (group[0] as any).autoLayoutChildResizing?.width === "fill"
          : (group[0] as any).autoLayoutChildResizing?.height === "fill";

        const groupSize = hasMainAxisFill
          ? fillElementSize
          : isHorizontal
          ? bounds[2] - bounds[0]
          : bounds[3] - bounds[1];
        return sum + groupSize;
      }, 0);

      // Calculate total size including gaps between elements
      const numericGap = getNumericGap(gap);
      const totalGaps = (sortedGroups.length - 1) * numericGap;
      const totalContentSize = totalGroupSize + totalGaps;

      const mainAlignment = isHorizontal
        ? alignment.horizontal
        : alignment.vertical;

      // Position the entire group based on alignment
      let groupStartPosition = mainPadding.start;
      switch (mainAlignment) {
        case "start":
          groupStartPosition = mainPadding.start;
          break;
        case "center":
          groupStartPosition =
            mainPadding.start + (contentMainSize - totalContentSize) / 2;
          break;
        case "end":
          groupStartPosition =
            mainPadding.start + contentMainSize - totalContentSize;
          break;
      }

      // Now distribute elements within the group with fixed gaps
      let currentPos = groupStartPosition;
      positions = sortedGroups.map(({ bounds, group }, index) => {
        const pos = currentPos;

        // Check if this group has fill elements
        const hasMainAxisFill = isHorizontal
          ? (group[0] as any).autoLayoutChildResizing?.width === "fill"
          : (group[0] as any).autoLayoutChildResizing?.height === "fill";

        const groupSize = hasMainAxisFill
          ? fillElementSize
          : isHorizontal
          ? bounds[2] - bounds[0]
          : bounds[3] - bounds[1];

        currentPos +=
          groupSize + (index < sortedGroups.length - 1 ? numericGap : 0);
        return pos;
      });
    } else {
      // For hug-content frames, use normal fixed gap positioning
      let currentPosition = mainPadding.start;
      positions = sortedGroups.map(({ bounds, group }, index) => {
        const pos = currentPosition;

        // Check if this group has fill elements
        const hasMainAxisFill = isHorizontal
          ? (group[0] as any).autoLayoutChildResizing?.width === "fill"
          : (group[0] as any).autoLayoutChildResizing?.height === "fill";

        const groupSize = hasMainAxisFill
          ? fillElementSize
          : isHorizontal
          ? bounds[2] - bounds[0]
          : bounds[3] - bounds[1];

        currentPosition +=
          groupSize + (index < sortedGroups.length - 1 ? (gap as number) : 0);
        return pos;
      });
    }
  }

  sortedGroups.forEach(({ group, bounds }, index) => {
    // -------------------------------------------------------------
    // Determine scaling required for this group when "fill" is involved.
    // -------------------------------------------------------------

    const originalGroupWidth = bounds[2] - bounds[0];
    const originalGroupHeight = bounds[3] - bounds[1];

    const groupResizing = (group[0] as any).autoLayoutChildResizing;
    const hasMainAxisFill = isHorizontal
      ? groupResizing?.width === "fill"
      : groupResizing?.height === "fill";

    const hasCrossAxisFill = isHorizontal
      ? groupResizing?.height === "fill"
      : groupResizing?.width === "fill";

    const groupMainTarget = hasMainAxisFill
      ? fillElementSize
      : isHorizontal
      ? originalGroupWidth
      : originalGroupHeight;

    const groupCrossTarget = hasCrossAxisFill
      ? contentCrossSize
      : isHorizontal
      ? originalGroupHeight
      : originalGroupWidth;

    const mainScale =
      groupMainTarget /
      Math.max(1, isHorizontal ? originalGroupWidth : originalGroupHeight);

    const crossScale =
      groupCrossTarget /
      Math.max(1, isHorizontal ? originalGroupHeight : originalGroupWidth);

    const scaleX = isHorizontal ? mainScale : crossScale;
    const scaleY = isHorizontal ? crossScale : mainScale;

    // Scaled cross-size used for alignment calculations
    const groupCrossSize = isHorizontal
      ? originalGroupHeight * scaleY
      : originalGroupWidth * scaleX;

    // -------------------------------------------------------------
    // Calculate main & cross axis offsets for this group so that it can
    // be positioned correctly inside the auto-layout frame.
    // -------------------------------------------------------------

    const mainAxisOffset =
      frameStart + positions[index] - bounds[isHorizontal ? 0 : 1];

    let crossAxisOffset = 0;
    const crossAlignment = isHorizontal
      ? alignment.vertical
      : alignment.horizontal;

    if (isMainAxisFixed) {
      switch (crossAlignment) {
        case "start":
          crossAxisOffset =
            frameCrossStart + crossPadding.start - bounds[isHorizontal ? 1 : 0];
          break;
        case "center":
          crossAxisOffset =
            frameCrossStart +
            crossPadding.start +
            (contentCrossSize - groupCrossSize) / 2 -
            bounds[isHorizontal ? 1 : 0];
          break;
        case "end":
          crossAxisOffset =
            frameCrossStart +
            frameCrossSize -
            crossPadding.end -
            groupCrossSize -
            bounds[isHorizontal ? 1 : 0];
          break;
      }
    } else {
      switch (crossAlignment) {
        case "start":
          crossAxisOffset =
            frameCrossStart + crossPadding.start - bounds[isHorizontal ? 1 : 0];
          break;
        case "center":
          crossAxisOffset =
            frameCrossStart +
            crossPadding.start +
            (contentCrossSize - groupCrossSize) / 2 -
            bounds[isHorizontal ? 1 : 0];
          break;
        case "end":
          crossAxisOffset =
            frameCrossStart +
            frameCrossSize -
            crossPadding.end -
            groupCrossSize -
            bounds[isHorizontal ? 1 : 0];
          break;
      }
    }

    // ------------------------------------------------------------------
    // Iterate through each element in the group so we can apply either
    // proportional scaling (for real groups) or the original fill logic
    // (for single-element "groups").
    // ------------------------------------------------------------------

    group.forEach((element, elementIndex) => {
      const isGrouped = group.length > 1;

      let newWidth = element.width;
      let newHeight = element.height;
      let newX: number;
      let newY: number;

      const childResizing = (element as any).autoLayoutChildResizing;

      if (isGrouped) {
        // -----------------------------------------
        // Proportional scaling for true groups
        // -----------------------------------------

        newWidth = element.width * scaleX;
        newHeight = element.height * scaleY;

        const newGroupX =
          bounds[0] + (isHorizontal ? mainAxisOffset : crossAxisOffset);
        const newGroupY =
          bounds[1] + (isHorizontal ? crossAxisOffset : mainAxisOffset);

        newX = newGroupX + (element.x - bounds[0]) * scaleX;
        newY = newGroupY + (element.y - bounds[1]) * scaleY;
      } else {
        // -----------------------------------------
        // Original behaviour for lone elements
        // -----------------------------------------

        // Handle child resizing for regular elements and auto-layout nested frames only
        // Don't resize nested frames without auto layout - preserve their dimensions
        const isNestedFrameWithoutAutoLayout =
          isFrameLikeElement(element) && !(element as any).autoLayout?.enabled;

        // Always allow explicit fill behavior, even for nested frames without auto layout
        // This allows users to manually set fill width/height via UI controls
        const shouldAllowFillBehavior =
          childResizing &&
          (!isNestedFrameWithoutAutoLayout ||
            childResizing.width === "fill" ||
            childResizing.height === "fill");

        if (shouldAllowFillBehavior) {
          if (isHorizontal) {
            if (childResizing.height === "fill") {
              newHeight = contentCrossSize;
            }
            if (childResizing.width === "fill") {
              newWidth = fillElementSize;
            }
          } else {
            if (childResizing.width === "fill") {
              newWidth = contentCrossSize;
            }
            if (childResizing.height === "fill") {
              newHeight = fillElementSize;
            }
          }
        }

        newX = element.x + (isHorizontal ? mainAxisOffset : crossAxisOffset);
        newY = element.y + (isHorizontal ? crossAxisOffset : mainAxisOffset);

        // Handle positioning for child element fill resizing
        // Allow fill positioning when element has explicit fill behavior
        if (shouldAllowFillBehavior) {
          if (isHorizontal && childResizing.height === "fill") {
            newY = frame.y + padding.top;
          }
          if (!isHorizontal && childResizing.width === "fill") {
            newX = frame.x + padding.left;
          }
        }
      }

      const newElement = newElementWith(element, {
        x: newX,
        y: newY,
        ...(newWidth !== element.width && { width: newWidth }),
        ...(newHeight !== element.height && { height: newHeight }),
      });

      updatedChildren.push(newElement);
    });
  });

  // Calculate new frame size based on resizing behavior using UPDATED children bounds
  let newFrameWidth = frame.width;
  let newFrameHeight = frame.height;

  // Create updated bounds map for each group after children have been resized
  const updatedGroupBounds = new Map<
    number,
    readonly [number, number, number, number]
  >();
  sortedGroups.forEach((_, index) => {
    const groupElements = sortedGroups[index].group;
    const updatedGroupChildren = groupElements.map(
      (el) => updatedChildren.find((updated) => updated.id === el.id) || el,
    );

    if (updatedGroupChildren.length > 0) {
      const updatedBounds = getCommonBounds(updatedGroupChildren);
      updatedGroupBounds.set(index, updatedBounds);
    }
  });

  // Check if any children have fill container set - this affects parent behavior

  // Resize frame width when set to "hug" using UPDATED children bounds
  if (autoLayout.resizing.width === "hug") {
    // Hug contents: resize frame to fit children + padding
    const contentWidth = isHorizontal
      ? positions.length > 0
        ? Math.max(
            ...positions.map((pos, index) => {
              const updatedBounds = updatedGroupBounds.get(index);
              const groupSize = updatedBounds
                ? updatedBounds[2] - updatedBounds[0]
                : sortedGroups[index].bounds[2] - sortedGroups[index].bounds[0];
              return pos + groupSize;
            }),
          ) + padding.right
        : padding.left + padding.right
      : Math.max(
          ...Array.from(updatedGroupBounds.values()).map(
            (bounds) => bounds[2] - bounds[0],
          ),
        ) +
        padding.left +
        padding.right;
    newFrameWidth = Math.max(contentWidth, 0);
  }
  // If resizing.width === "fixed" or children have fill, keep current width

  // Height calculation using UPDATED children bounds
  if (autoLayout.resizing.height === "hug") {
    // Hug contents: resize frame to fit children + padding
    const contentHeight = isHorizontal
      ? Math.max(
          ...Array.from(updatedGroupBounds.values()).map(
            (bounds) => bounds[3] - bounds[1],
          ),
        ) +
        padding.top +
        padding.bottom
      : positions.length > 0
      ? Math.max(
          ...positions.map((pos, index) => {
            const updatedBounds = updatedGroupBounds.get(index);
            const groupSize = updatedBounds
              ? updatedBounds[3] - updatedBounds[1]
              : sortedGroups[index].bounds[3] - sortedGroups[index].bounds[1];
            return pos + groupSize;
          }),
        ) + padding.bottom
      : padding.top + padding.bottom;
    newFrameHeight = Math.max(contentHeight, 0);
  }
  // If resizing.height === "fixed" or children have fill, keep current height

  const updatedFrame = newElementWith(frame, {
    width: newFrameWidth,
    height: newFrameHeight,
  });

  // Handle nested frames: if any child frames moved, update their children
  const finalUpdatedChildren = [...updatedChildren];
  const allUpdatedElements = new Map<string, ExcalidrawElement>();

  // Start with the direct children
  for (const child of finalUpdatedChildren) {
    allUpdatedElements.set(child.id, child);

    // ---------------------------------------------------------------------
    // Ensure bound-text moves together with its container
    // ---------------------------------------------------------------------
    if (hasBoundTextElement(child)) {
      const boundText = getBoundTextElement(child, elementsMap);
      if (boundText) {
        const { x: btX, y: btY } = computeBoundTextPosition(
          child,
          boundText,
          elementsMap,
        );

        const movedBoundText = newElementWith(boundText, {
          x: btX,
          y: btY,
        });

        allUpdatedElements.set(movedBoundText.id, movedBoundText);
      }
    }
  }

  // Check for child frames that have moved and update their children
  if (allElements) {
    // Create a map of original children by ID for accurate comparison
    const originalChildrenMap = new Map<string, ExcalidrawElement>();
    for (const child of children) {
      originalChildrenMap.set(child.id, child);
    }

    for (const updatedChild of finalUpdatedChildren) {
      const originalChild = originalChildrenMap.get(updatedChild.id);

      // If this child is a frame and it moved, update its children
      if (originalChild && isFrameLikeElement(originalChild)) {
        const hasMoved =
          originalChild.x !== updatedChild.x ||
          originalChild.y !== updatedChild.y;

        // Also check if the frame changed parents (different frameId)
        const hasChangedParent = originalChild.frameId !== updatedChild.frameId;

        if (hasMoved || hasChangedParent) {
          // Create updated elements map that includes all current changes
          const updatedElementsMap = new Map<string, ExcalidrawElement>();

          // First, add all original elements
          for (const element of allElements) {
            updatedElementsMap.set(element.id, element);
          }

          // Then override with any updated elements we have so far
          for (const [id, element] of allUpdatedElements) {
            updatedElementsMap.set(id, element);
          }

          // Also add the final updated children
          for (const child of finalUpdatedChildren) {
            updatedElementsMap.set(child.id, child);
          }

          // Use originalChild (from children) as the true starting position for auto-layout
          // This represents the frame's position before this auto-layout calculation
          const frameOriginalPosition = originalChild;

          // Create a scene interface for the moveFrameChildren function
          const scene = {
            mutateElement: (element: ExcalidrawElement, updates: any) => {
              const newElement = newElementWith(element, updates);
              allUpdatedElements.set(element.id, newElement);
              updatedElementsMap.set(element.id, newElement);
              return newElement;
            },
            getNonDeletedElements: () =>
              Array.from(updatedElementsMap.values()).filter(
                (el) => !el.isDeleted,
              ),
          };

          // Move the children using the original position from auto-layout calculation
          const movedChildren = moveFrameChildren(
            frameOriginalPosition as ExcalidrawFrameLikeElement,
            updatedChild as ExcalidrawFrameLikeElement,
            Array.from(updatedElementsMap.values()),
            updatedElementsMap,
            scene,
          );

          // Ensure all moved children are properly added to the final results
          for (const [childId, movedChild] of movedChildren) {
            allUpdatedElements.set(childId, movedChild);
          }
        }
      }
    }
  }

  // If manual reordering was detected, return the elements in their new order
  // so the document order can be updated to match the visual order
  const reorderedElements = hasManualReordering
    ? sortedGroups.flatMap(({ group }) => group)
    : undefined;

  return {
    updatedChildren: finalUpdatedChildren,
    updatedFrame,
    reorderedElements,
    allUpdatedElements: Array.from(allUpdatedElements.values()),
  };
};

/**
 * Apply auto layout to a frame and its children
 */
export const applyAutoLayout = (
  frame: ExcalidrawFrameElement,
  allElements: readonly ExcalidrawElement[],
  elementsMap: ElementsMap,
): ExcalidrawElement[] => {
  if (!isAutoLayoutFrame(frame)) {
    return [...allElements];
  }

  const children = getAutoLayoutChildren(frame, allElements, elementsMap);
  const { updatedFrame, reorderedElements, allUpdatedElements } =
    calculateAutoLayout(frame, children, elementsMap, allElements);

  // Create a map of updated elements (including moved children from nested frames)
  const updatedElementsMap = new Map<string, ExcalidrawElement>();
  updatedElementsMap.set(updatedFrame.id, updatedFrame);

  // Add all updated elements (including moved children from nested frames)
  allUpdatedElements.forEach((element) => {
    updatedElementsMap.set(element.id, element);
  });

  let result = allElements.map(
    (element) => updatedElementsMap.get(element.id) || element,
  );

  // If elements were manually reordered, update the document order to match
  if (reorderedElements) {
    result = updateDocumentOrder(result, reorderedElements);
  }

  // Skip validateAndFixNestedFrames for auto-layout contexts
  // Auto-layout handles nested frame positioning correctly, and validateAndFixNestedFrames
  // can interfere by moving frames without moving their children

  return result;
};

/**
 * Update the document order to match the visual order after manual reordering
 */
const updateDocumentOrder = (
  allElements: ExcalidrawElement[],
  reorderedElements: ExcalidrawElement[],
): ExcalidrawElement[] => {
  if (reorderedElements.length === 0) {
    return allElements;
  }

  // Get the frame ID from the first reordered element
  const frameId = reorderedElements[0].frameId;
  if (!frameId) {
    return allElements;
  }

  // Create a set of reordered element IDs for quick lookup
  const reorderedIds = new Set(reorderedElements.map((el) => el.id));

  // Find all elements that belong to this frame but aren't in the reordered list
  const otherFrameElements = allElements.filter(
    (el) => el.frameId === frameId && !reorderedIds.has(el.id) && !el.isDeleted,
  );

  // Build the new array
  const result: ExcalidrawElement[] = [];
  let frameInserted = false;

  for (const element of allElements) {
    // When we encounter the frame, insert it and all its children in the new order
    if (element.id === frameId) {
      result.push(element); // Add the frame itself
      result.push(...reorderedElements); // Add reordered elements in new order
      result.push(...otherFrameElements); // Add other frame elements
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      frameInserted = true;
    }
    // Skip elements that belong to this frame (they were already added)
    else if (element.frameId === frameId) {
      continue;
    }
    // Add all other elements
    else {
      result.push(element);
    }
  }

  // Ensure fractional indices remain consistent with the new visual order
  // by synchronizing moved elements' indices with their positions in the
  // `result` array.
  const movedElementsMap = arrayToMap(reorderedElements);
  syncMovedIndices(result, movedElementsMap);

  return result;
};

/**
 * Analyze existing layout to detect optimal auto layout settings
 */
const detectLayoutSettings = (
  frame: ExcalidrawFrameElement,
  children: ExcalidrawElement[],
): Partial<AutoLayoutProperties> => {
  if (children.length === 0) {
    return DEFAULT_AUTO_LAYOUT_PROPERTIES;
  }

  // Get bounds for all children
  const childBounds = children.map((child) => getCommonBounds([child]));

  // Analyze direction by looking at element arrangement
  let direction: AutoLayoutDirection = "vertical";

  if (children.length >= 2) {
    // Calculate center points
    const centers = childBounds.map((bounds) => ({
      x: (bounds[0] + bounds[2]) / 2,
      y: (bounds[1] + bounds[3]) / 2,
    }));

    // Calculate variance in x and y positions
    const avgX = centers.reduce((sum, c) => sum + c.x, 0) / centers.length;
    const avgY = centers.reduce((sum, c) => sum + c.y, 0) / centers.length;

    const varianceX =
      centers.reduce((sum, c) => sum + Math.pow(c.x - avgX, 2), 0) /
      centers.length;
    const varianceY =
      centers.reduce((sum, c) => sum + Math.pow(c.y - avgY, 2), 0) /
      centers.length;

    // If elements vary more horizontally, it's likely a horizontal layout
    direction = varianceX > varianceY ? "horizontal" : "vertical";
  }

  // Detect gap by analyzing spacing between elements
  let detectedGap = 10; // Default

  if (children.length >= 2) {
    const sortedBounds = [...childBounds].sort((a, b) =>
      direction === "horizontal" ? a[0] - b[0] : a[1] - b[1],
    );

    const gaps: number[] = [];
    let hasOverlaps = false;

    for (let i = 0; i < sortedBounds.length - 1; i++) {
      const current = sortedBounds[i];
      const next = sortedBounds[i + 1];

      const gap =
        direction === "horizontal"
          ? next[0] - current[2] // left edge of next - right edge of current
          : next[1] - current[3]; // top edge of next - bottom edge of current

      if (gap < 0) {
        hasOverlaps = true;
      } else if (gap >= 0) {
        gaps.push(gap);
      }
    }

    if (gaps.length > 0) {
      // Use median gap to avoid outliers
      gaps.sort((a, b) => a - b);
      detectedGap = Math.round(gaps[Math.floor(gaps.length / 2)]);
    }

    // If there are overlaps or very inconsistent spacing, use a clean default
    if (hasOverlaps || gaps.length === 0) {
      detectedGap = 16; // Clean default gap
    } else {
      // Ensure minimum gap for readability
      detectedGap = Math.max(detectedGap, 8);
    }
  }

  // Detect padding by analyzing space between frame edges and children
  const frameBounds = getCommonBounds([frame]);
  const allChildrenBounds = getCommonBounds(children);

  // Calculate detected padding with intelligent defaults
  const rawPadding = {
    top: Math.max(0, Math.round(allChildrenBounds[1] - frameBounds[1])),
    right: Math.max(0, Math.round(frameBounds[2] - allChildrenBounds[2])),
    bottom: Math.max(0, Math.round(frameBounds[3] - allChildrenBounds[3])),
    left: Math.max(0, Math.round(allChildrenBounds[0] - frameBounds[0])),
  };

  // Normalize padding values - if they're very inconsistent, use clean defaults
  const paddingValues = Object.values(rawPadding);
  const avgPadding =
    paddingValues.reduce((sum, p) => sum + p, 0) / paddingValues.length;
  const hasInconsistentPadding = paddingValues.some(
    (p) => Math.abs(p - avgPadding) > avgPadding * 0.5,
  );

  const detectedPadding = hasInconsistentPadding
    ? {
        top: 16,
        right: 16,
        bottom: 16,
        left: 16,
      }
    : {
        top: Math.max(8, rawPadding.top),
        right: Math.max(8, rawPadding.right),
        bottom: Math.max(8, rawPadding.bottom),
        left: Math.max(8, rawPadding.left),
      };

  // Detect alignment by analyzing how elements are positioned relative to frame
  let alignmentPosition: AutoLayoutAlignmentPosition = "center-center";

  if (children.length > 0) {
    const frameCenter = {
      x: (frameBounds[0] + frameBounds[2]) / 2,
      y: (frameBounds[1] + frameBounds[3]) / 2,
    };

    const childrenCenter = {
      x: (allChildrenBounds[0] + allChildrenBounds[2]) / 2,
      y: (allChildrenBounds[1] + allChildrenBounds[3]) / 2,
    };

    // Determine horizontal alignment
    const horizontalThreshold = (frameBounds[2] - frameBounds[0]) * 0.1; // 10% threshold
    let horizontal = "center";
    if (childrenCenter.x < frameCenter.x - horizontalThreshold) {
      horizontal = "left";
    } else if (childrenCenter.x > frameCenter.x + horizontalThreshold) {
      horizontal = "right";
    }

    // Determine vertical alignment
    const verticalThreshold = (frameBounds[3] - frameBounds[1]) * 0.1; // 10% threshold
    let vertical = "center";
    if (childrenCenter.y < frameCenter.y - verticalThreshold) {
      vertical = "top";
    } else if (childrenCenter.y > frameCenter.y + verticalThreshold) {
      vertical = "bottom";
    }

    // Map to alignment position
    alignmentPosition =
      `${vertical}-${horizontal}` as AutoLayoutAlignmentPosition;
  }

  return {
    direction,
    gap: detectedGap,
    padding: detectedPadding,
    alignmentPosition,
    resizing: {
      width: "hug",
      height: "hug",
    },
  };
};

/**
 * Toggle auto layout on a frame with intelligent detection of layout properties
 */
export const toggleAutoLayout = (
  frame: ExcalidrawFrameElement,
  enabled: boolean,
  allElements?: readonly ExcalidrawElement[],
  elementsMap?: ElementsMap,
): ExcalidrawFrameElement => {
  const currentAutoLayout = frame.autoLayout || DEFAULT_AUTO_LAYOUT_PROPERTIES;

  if (enabled && allElements && elementsMap) {
    // When enabling auto layout, detect optimal settings from current layout
    const children = getAutoLayoutChildren(frame, allElements, elementsMap);
    const detectedSettings = detectLayoutSettings(frame, children);

    return newElementWith(frame, {
      autoLayout: {
        ...DEFAULT_AUTO_LAYOUT_PROPERTIES,
        ...detectedSettings,
        enabled: true,
      },
    });
  }

  // When disabling or no context provided, just toggle the enabled state
  return newElementWith(frame, {
    autoLayout: {
      ...currentAutoLayout,
      enabled,
    },
  });
};

/**
 * Update auto layout properties of a frame
 */
export const updateAutoLayoutProperties = (
  frame: ExcalidrawFrameElement,
  updates: Partial<AutoLayoutProperties>,
): ExcalidrawFrameElement => {
  const currentAutoLayout = frame.autoLayout || DEFAULT_AUTO_LAYOUT_PROPERTIES;

  return newElementWith(frame, {
    autoLayout: {
      ...currentAutoLayout,
      ...updates,
      padding: updates.padding
        ? { ...currentAutoLayout.padding, ...updates.padding }
        : currentAutoLayout.padding,
      resizing: updates.resizing
        ? { ...currentAutoLayout.resizing, ...updates.resizing }
        : currentAutoLayout.resizing,
    },
  });
};

/**
 * Get default auto layout properties for a new frame
 */
export const getDefaultAutoLayoutProperties = (): AutoLayoutProperties => {
  return { ...DEFAULT_AUTO_LAYOUT_PROPERTIES };
};

/**
 * Check if elements in a frame have changed and need auto layout reapplication
 */
const getFrameChildrenIds = (
  frame: ExcalidrawFrameElement,
  allElements: readonly ExcalidrawElement[],
): Set<string> => {
  return new Set(
    allElements
      .filter((element) => element.frameId === frame.id && !element.isDeleted)
      .map((element) => element.id),
  );
};

/**
 * Get a snapshot of frame children with their dimensions for change detection
 */
const getFrameChildrenSnapshot = (
  frame: ExcalidrawFrameElement,
  allElements: readonly ExcalidrawElement[],
): Map<string, { width: number; height: number; x: number; y: number }> => {
  const snapshot = new Map();

  allElements
    .filter((element) => element.frameId === frame.id && !element.isDeleted)
    .forEach((element) => {
      snapshot.set(element.id, {
        width: element.width,
        height: element.height,
        x: element.x,
        y: element.y,
      });
    });

  return snapshot;
};

/**
 * Compare two snapshots to detect changes in element dimensions or positions
 */
const snapshotsEqual = (
  snapshot1: Map<
    string,
    { width: number; height: number; x: number; y: number }
  >,
  snapshot2: Map<
    string,
    { width: number; height: number; x: number; y: number }
  >,
): boolean => {
  if (snapshot1.size !== snapshot2.size) {
    return false;
  }

  for (const [id, props1] of snapshot1) {
    const props2 = snapshot2.get(id);
    if (
      !props2 ||
      props1.width !== props2.width ||
      props1.height !== props2.height ||
      props1.x !== props2.x ||
      props1.y !== props2.y
    ) {
      return false;
    }
  }

  return true;
};

/**
 * Apply auto layout to all frames that have auto layout enabled
 */
export const reapplyAutoLayoutToAllFrames = (
  allElements: readonly ExcalidrawElement[],
  elementsMap: ElementsMap,
): readonly ExcalidrawElement[] => {
  let updatedElements = allElements.slice();
  let hasChanges = false;

  // Find all frames with auto layout enabled
  const autoLayoutFrames = allElements.filter(
    (element): element is ExcalidrawFrameElement =>
      element.type === "frame" &&
      element.autoLayout?.enabled === true &&
      !element.isDeleted,
  );

  // Sort frames to ensure parents are processed before children
  autoLayoutFrames.sort((a, b) => {
    const aIsChildOfB = b.frameId === a.id;
    const bIsChildOfA = a.frameId === b.id;

    if (aIsChildOfB) {
      return 1; // a is child of b, so b comes first
    }
    if (bIsChildOfA) {
      return -1; // b is child of a, so a comes first
    }

    // If no direct parent-child relationship, check if one is a descendant of the other
    const aChildren = getAllNestedFrameChildren(allElements, a.id);
    const bChildren = getAllNestedFrameChildren(allElements, b.id);

    if (aChildren.some((child) => child.id === b.id)) {
      return -1; // b is a descendant of a, so a comes first
    }
    if (bChildren.some((child) => child.id === a.id)) {
      return 1; // a is a descendant of b, so b comes first
    }

    return 0; // No hierarchical relationship or same level
  });

  for (const frame of autoLayoutFrames) {
    const children = getAutoLayoutChildren(frame, updatedElements, elementsMap);
    const {
      updatedChildren,
      updatedFrame,
      reorderedElements,
      allUpdatedElements,
    } = calculateAutoLayout(frame, children, elementsMap, updatedElements);

    // Check if any changes were made
    const frameChanged =
      updatedFrame.width !== frame.width ||
      updatedFrame.height !== frame.height ||
      updatedFrame.x !== frame.x ||
      updatedFrame.y !== frame.y;

    const childrenChanged = updatedChildren.some((child, index) => {
      const originalChild = children[index];
      return (
        originalChild &&
        (child.x !== originalChild.x ||
          child.y !== originalChild.y ||
          child.width !== originalChild.width ||
          child.height !== originalChild.height)
      );
    });

    // Check if nested children were moved
    const nestedChildrenChanged =
      allUpdatedElements.length > updatedChildren.length;

    if (
      frameChanged ||
      childrenChanged ||
      nestedChildrenChanged ||
      reorderedElements
    ) {
      hasChanges = true;

      // Create a map of updated elements (including moved children from nested frames)
      const updatedElementsMap = new Map<string, ExcalidrawElement>();
      updatedElementsMap.set(updatedFrame.id, updatedFrame);
      allUpdatedElements.forEach((element) => {
        updatedElementsMap.set(element.id, element);
      });

      // Apply updates to the elements array
      updatedElements = updatedElements.map(
        (element) => updatedElementsMap.get(element.id) || element,
      );

      // If elements were manually reordered, update the document order to match
      if (reorderedElements) {
        updatedElements = updateDocumentOrder(
          updatedElements,
          reorderedElements,
        );
      }
    }
  }

  return hasChanges ? updatedElements : allElements;
};

/**
 * Setup auto layout listener for a scene to automatically reapply layout when elements change
 */
export const setupAutoLayoutListener = (scene: any): (() => void) => {
  const previousFrameChildrenMap = new Map<string, Set<string>>();
  const previousFrameSnapshotMap = new Map<
    string,
    Map<string, { width: number; height: number; x: number; y: number }>
  >();
  let isApplyingAutoLayout = false;
  let wasDragging = false;
  let wasResizing = false;

  const updateListener = () => {
    // Skip if we're currently applying auto layout to avoid infinite loops
    if (isApplyingAutoLayout) {
      return;
    }

    // Check if elements are being dragged or resized to prevent jankiness during these operations
    const appState = scene.getAppState?.();
    const isDragging = appState?.selectedElementsAreBeingDragged;
    const isResizing = appState?.resizingElement !== null;

    // If we were dragging/resizing and now we're not, force a layout update
    const dragJustEnded = wasDragging && !isDragging;
    const resizeJustEnded = wasResizing && !isResizing;
    wasDragging = isDragging || false;
    wasResizing = isResizing || false;

    if (isDragging || isResizing) {
      return;
    }

    const allElements = scene.getElementsIncludingDeleted();
    const elementsMap = scene.getNonDeletedElementsMap();

    // Find all frames with auto layout enabled
    const autoLayoutFrames = allElements.filter(
      (element: ExcalidrawElement): element is ExcalidrawFrameElement =>
        element.type === "frame" &&
        element.autoLayout?.enabled === true &&
        !element.isDeleted,
    );

    // Initialize tracking for any new auto-layout frames that aren't tracked yet
    for (const frame of autoLayoutFrames) {
      if (!previousFrameChildrenMap.has(frame.id)) {
        const children = getFrameChildrenIds(frame, allElements);
        const snapshot = getFrameChildrenSnapshot(frame, allElements);
        previousFrameChildrenMap.set(frame.id, children);
        previousFrameSnapshotMap.set(frame.id, snapshot);
      }
    }

    let shouldReapplyLayout = false;

    // Check if any frame's children have changed (addition/deletion) or resized
    for (const frame of autoLayoutFrames) {
      const currentChildren = getFrameChildrenIds(frame, allElements);
      const previousChildren = previousFrameChildrenMap.get(frame.id);

      const currentSnapshot = getFrameChildrenSnapshot(frame, allElements);
      const previousSnapshot = previousFrameSnapshotMap.get(frame.id);

      // Check for element addition/deletion
      const childrenChanged =
        !previousChildren || !setsEqual(currentChildren, previousChildren);

      // Check for element resizing/repositioning (but only if it's not due to auto layout itself)
      const dimensionsChanged =
        !previousSnapshot || !snapshotsEqual(currentSnapshot, previousSnapshot);

      if (childrenChanged || dimensionsChanged) {
        shouldReapplyLayout = true;
        previousFrameChildrenMap.set(frame.id, currentChildren);
        previousFrameSnapshotMap.set(frame.id, currentSnapshot);
      }
    }

    // Clean up deleted frames from our tracking
    const currentFrameIds = new Set(
      autoLayoutFrames.map((f: ExcalidrawFrameElement) => f.id),
    );
    for (const [frameId] of previousFrameChildrenMap) {
      if (!currentFrameIds.has(frameId)) {
        previousFrameChildrenMap.delete(frameId);
        previousFrameSnapshotMap.delete(frameId);
      }
    }

    // Reapply auto layout if needed or if dragging/resizing just ended
    if (shouldReapplyLayout || dragJustEnded || resizeJustEnded) {
      const updatedElements = reapplyAutoLayoutToAllFrames(
        allElements,
        elementsMap,
      );
      if (updatedElements !== allElements) {
        // Set flag to prevent recursive updates
        isApplyingAutoLayout = true;

        // Use setTimeout to avoid recursive updates and reset flag
        setTimeout(() => {
          // Use updateScene instead of replaceAllElements to properly capture history
          // Auto-layout changes should be undoable as part of the user action that triggered them
          // This ensures that when you resize a nested frame and then undo, both the resize
          // and the auto-layout positioning changes are properly reverted
          if (scene.updateScene) {
            scene.updateScene({
              elements: updatedElements,
              captureUpdate: CaptureUpdateAction.EVENTUALLY,
            });
          } else {
            // Fallback for older scene interface
            scene.replaceAllElements(updatedElements);
          }

          // Reset flag and update our tracking with the new state
          setTimeout(() => {
            isApplyingAutoLayout = false;

            // Update our tracking after auto layout has been applied
            const newElements = scene.getElementsIncludingDeleted();
            const newAutoLayoutFrames = newElements.filter(
              (element: ExcalidrawElement): element is ExcalidrawFrameElement =>
                element.type === "frame" &&
                element.autoLayout?.enabled === true &&
                !element.isDeleted,
            );

            for (const frame of newAutoLayoutFrames) {
              const newChildren = getFrameChildrenIds(frame, newElements);
              const newSnapshot = getFrameChildrenSnapshot(frame, newElements);
              previousFrameChildrenMap.set(frame.id, newChildren);
              previousFrameSnapshotMap.set(frame.id, newSnapshot);
            }

            // Trigger scene update to ensure visual re-rendering after auto-layout changes
            // This ensures nested frame children are visually positioned correctly
            if (scene.triggerUpdate) {
              scene.triggerUpdate();
            }
          }, 0);
        }, 0);
      } else if (dragJustEnded || resizeJustEnded) {
        // Even if no layout changes were needed, update our tracking after drag/resize ends
        for (const frame of autoLayoutFrames) {
          const newChildren = getFrameChildrenIds(frame, allElements);
          const newSnapshot = getFrameChildrenSnapshot(frame, allElements);
          previousFrameChildrenMap.set(frame.id, newChildren);
          previousFrameSnapshotMap.set(frame.id, newSnapshot);
        }
      }
    }
  };

  // Add listener to scene
  const removeListener = scene.onUpdate(updateListener);

  return removeListener;
};

/**
 * Utility function to compare two sets for equality
 */
const setsEqual = <T>(set1: Set<T>, set2: Set<T>): boolean => {
  if (set1.size !== set2.size) {
    return false;
  }
  for (const item of set1) {
    if (!set2.has(item)) {
      return false;
    }
  }
  return true;
};

/**
 * Reorder auto-layout elements within a frame
 */
export const reorderAutoLayoutElements = (
  frame: ExcalidrawFrameElement,
  selectedElements: ExcalidrawElement[],
  operation: "first" | "last" | "previous" | "next",
  allElements: readonly ExcalidrawElement[],
  elementsMap: ElementsMap,
): ExcalidrawElement[] => {
  // Safety check: validate inputs
  if (!frame?.autoLayout?.enabled) {
    console.warn(
      "reorderAutoLayoutElements: frame does not have auto layout enabled",
    );
    return [...allElements];
  }

  if (!selectedElements?.length) {
    console.warn("reorderAutoLayoutElements: no elements selected");
    return [...allElements];
  }

  if (!allElements?.length) {
    console.warn("reorderAutoLayoutElements: no elements provided");
    return [...allElements];
  }

  // Get all children of the frame
  const children = getAutoLayoutChildren(frame, allElements, elementsMap);

  if (children.length === 0) {
    console.warn("reorderAutoLayoutElements: frame has no children");
    return [...allElements];
  }

  // Safety check: ensure selected elements are actually children of this frame
  const validSelectedElements = selectedElements.filter((sel) =>
    children.some((child) => child.id === sel.id),
  );

  if (validSelectedElements.length === 0) {
    console.warn(
      "reorderAutoLayoutElements: no selected elements are children of this frame",
    );
    return [...allElements];
  }

  // Group elements to handle grouped items as single units
  const groups = getMaximumGroups(children, elementsMap, true); // exclude bound text for auto layout

  // Safety check: ensure groups are valid
  if (!groups?.length) {
    console.warn("reorderAutoLayoutElements: no valid groups found");
    return [...allElements];
  }

  // Find which groups contain selected elements
  const selectedGroups = groups.filter(
    (group) =>
      group?.length &&
      group.some((el) => {
        // Check if the element itself is selected
        return validSelectedElements.some((sel) => sel.id === el.id);
      }),
  );

  if (selectedGroups.length === 0) {
    console.warn(
      "reorderAutoLayoutElements: no groups contain selected elements",
    );
    return [...allElements];
  }

  // Create a new order based on the operation
  let newGroupOrder: ExcalidrawElement[][] = [];

  // For each selected group, determine its new position
  if (operation === "first") {
    // Put selected groups at the beginning
    newGroupOrder = [
      ...selectedGroups,
      ...groups.filter((g) => !selectedGroups.includes(g)),
    ];
  } else if (operation === "last") {
    // Put selected groups at the end
    newGroupOrder = [
      ...groups.filter((g) => !selectedGroups.includes(g)),
      ...selectedGroups,
    ];
  } else {
    // For previous/next, we need to handle each selected group individually
    newGroupOrder = [...groups];

    // Process each selected group
    for (const selectedGroup of selectedGroups) {
      const currentIndex = newGroupOrder.indexOf(selectedGroup);

      if (operation === "previous" && currentIndex > 0) {
        // Move one position earlier
        newGroupOrder.splice(currentIndex, 1);
        newGroupOrder.splice(currentIndex - 1, 0, selectedGroup);
      } else if (
        operation === "next" &&
        currentIndex < newGroupOrder.length - 1
      ) {
        // Move one position later
        newGroupOrder.splice(currentIndex, 1);
        newGroupOrder.splice(currentIndex + 1, 0, selectedGroup);
      }
    }
  }

  // Flatten the new group order to get reordered elements
  const reorderedElements = newGroupOrder.flatMap((group) => group);

  // First update the document order
  let result = updateDocumentOrder([...allElements], reorderedElements);

  // Create fresh elementsMap after document reordering for accurate position calculations
  const freshElementsMap = arrayToMap(result.filter((el) => !el.isDeleted));

  // Safety check: ensure frame still exists and has valid auto layout
  const updatedFrame = freshElementsMap.get(frame.id) as ExcalidrawFrameElement;
  if (!updatedFrame?.autoLayout) {
    console.warn(
      "reorderAutoLayoutElements: frame lost auto layout properties during reordering",
    );
    return result;
  }

  // Now we need to update the positions of elements to match their new order
  // This prevents the manual reordering detection from re-sorting them
  const direction = updatedFrame.autoLayout.direction;
  const gap = getNumericGap(updatedFrame.autoLayout.gap);
  const padding = updatedFrame.autoLayout.padding;

  // Calculate new positions for the reordered elements
  const isHorizontal = direction === "horizontal";
  let currentPos = isHorizontal
    ? updatedFrame.x + padding.left
    : updatedFrame.y + padding.top;

  const updatedElementsMap = new Map<string, ExcalidrawElement>();

  newGroupOrder.forEach((group, groupIndex) => {
    const groupBounds = getCommonBounds(group);
    const groupSize = isHorizontal
      ? groupBounds[2] - groupBounds[0]
      : groupBounds[3] - groupBounds[1];

    // Calculate offset to move this group to its new position
    const currentGroupPos = isHorizontal ? groupBounds[0] : groupBounds[1];
    const offset = currentPos - currentGroupPos;

    // Store original positions before updating for moveFrameChildren
    const originalPositions = new Map<string, { x: number; y: number }>();

    // Update each element in the group
    group.forEach((element) => {
      // Store original position for nested frame children handling
      originalPositions.set(element.id, { x: element.x, y: element.y });

      const newElement = newElementWith(element, {
        x: element.x + (isHorizontal ? offset : 0),
        y: element.y + (isHorizontal ? 0 : offset),
      });
      updatedElementsMap.set(element.id, newElement);

      // If this element has bound text, move the bound text with it
      if (hasBoundTextElement(element)) {
        const boundTextElement = getBoundTextElement(element, freshElementsMap);
        if (boundTextElement) {
          try {
            // Instead of just offsetting, recalculate the proper bound text position
            // relative to the moved container
            const { x: newBoundTextX, y: newBoundTextY } =
              computeBoundTextPosition(
                newElement,
                boundTextElement,
                freshElementsMap,
              );

            // Safety check: ensure coordinates are valid numbers
            if (
              typeof newBoundTextX === "number" &&
              typeof newBoundTextY === "number" &&
              !isNaN(newBoundTextX) &&
              !isNaN(newBoundTextY)
            ) {
              const newBoundText = newElementWith(boundTextElement, {
                x: newBoundTextX,
                y: newBoundTextY,
              });
              updatedElementsMap.set(boundTextElement.id, newBoundText);
            } else {
              console.warn(
                "reorderAutoLayoutElements: invalid bound text coordinates calculated",
              );
            }
          } catch (error) {
            console.warn(
              "reorderAutoLayoutElements: error calculating bound text position:",
              error,
            );
          }
        }
      }
    });

    // Handle nested frame children for any frames that were moved
    group.forEach((element) => {
      if (isFrameLikeElement(element) && offset !== 0) {
        const originalPos = originalPositions.get(element.id)!;
        const updatedElement = updatedElementsMap.get(
          element.id,
        )! as ExcalidrawFrameLikeElement;

        // Create a temporary scene interface for moveFrameChildren
        const tempScene = {
          mutateElement: (el: ExcalidrawElement, updates: any) => {
            const newEl = newElementWith(el, updates);
            updatedElementsMap.set(el.id, newEl);
            return newEl;
          },
          getNonDeletedElements: () =>
            Array.from(updatedElementsMap.values()).filter(
              (el) => !el.isDeleted,
            ),
        };

        // Create original frame with original position for moveFrameChildren
        const originalFrame = newElementWith(
          element,
          originalPos,
        ) as ExcalidrawFrameLikeElement;

        // Move nested frame children
        const movedChildren = moveFrameChildren(
          originalFrame,
          updatedElement,
          result,
          new Map(
            result.map((el) => [el.id, updatedElementsMap.get(el.id) || el]),
          ),
          tempScene,
        );

        // Apply moved children to the result
        for (const [childId, movedChild] of movedChildren) {
          updatedElementsMap.set(childId, movedChild);
        }
      }
    });

    // Move to next position
    currentPos += groupSize + (groupIndex < newGroupOrder.length - 1 ? gap : 0);
  });

  // Apply the position updates
  result = result.map((el) => updatedElementsMap.get(el.id) || el);

  // Finally apply auto layout to ensure everything is properly aligned
  let finalResult: ExcalidrawElement[];
  try {
    finalResult = applyAutoLayout(updatedFrame, result, freshElementsMap);
  } catch (error) {
    console.warn(
      "reorderAutoLayoutElements: error during final auto layout application:",
      error,
    );
    finalResult = result; // fallback to result without final auto layout
  }

  // Create fresh elementsMap for final bound text position calculations
  const finalElementsMap = arrayToMap(
    finalResult.filter((el) => !el.isDeleted),
  );

  // After auto layout, ensure all bound text elements are properly positioned
  // relative to their containers
  const finalUpdatedElementsMap = new Map<string, ExcalidrawElement>();

  finalResult.forEach((element) => {
    if (hasBoundTextElement(element)) {
      const boundTextElement = getBoundTextElement(element, finalElementsMap);
      if (boundTextElement) {
        try {
          const { x: correctX, y: correctY } = computeBoundTextPosition(
            element,
            boundTextElement,
            finalElementsMap,
          );

          // Safety check: ensure coordinates are valid
          if (
            typeof correctX === "number" &&
            typeof correctY === "number" &&
            !isNaN(correctX) &&
            !isNaN(correctY)
          ) {
            // Only update if position changed
            if (
              boundTextElement.x !== correctX ||
              boundTextElement.y !== correctY
            ) {
              const correctedBoundText = newElementWith(boundTextElement, {
                x: correctX,
                y: correctY,
              });
              finalUpdatedElementsMap.set(
                boundTextElement.id,
                correctedBoundText,
              );
            }
          }
        } catch (error) {
          console.warn(
            "reorderAutoLayoutElements: error in final bound text correction:",
            error,
          );
        }
      }
    }
  });

  // Apply any bound text corrections
  const correctedResult = finalResult.map(
    (el) => finalUpdatedElementsMap.get(el.id) || el,
  );

  return correctedResult;
};
